import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import SocialLogin from '../../../components/react/social_login/social_login';
import Spinner from '../../../components/react/spinner/spinner';
import { checkUserEmail, registerUserRequest } from '../../services/user.api.service';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import { MEMBERSHIPS_WITH_LANDING_PAGE } from '../../constants/membership_flow.constants';
import TermsAndConditions from '../../../components/react/terms_and_conditions/terms_and_conditions';
import { createQueryString } from '../../../utilities/utilities';
import classNames from 'classnames';
import {
  IsSignedIn,
  PackageShortName,
  PaymentPage,
  ReturnTo,
  SetEmailRecipient,
  Text1,
  Text2,
} from '../../types/membership_flow.types';
const REGISTER_PATH = '/register';

import './default_register_form.scss';

interface Props {
  text1: Text1;
  text2: Text2;
  packageShortName: PackageShortName;
  authToken: string;
  paymentPage: PaymentPage;
  returnTo: ReturnTo;
  isSignedIn: IsSignedIn;
  setEmailRecipient: SetEmailRecipient;
}

const DefaultRegisterForm: React.FC<Props> = ({
  text1,
  text2,
  packageShortName,
  authToken,
  paymentPage,
  returnTo,
  isSignedIn,
  setEmailRecipient,
}) => {
  const navigate = useNavigate();
  const [isCheckingInitialRedirection, setIsCheckingInitialRedirection] = useState(true);
  const [alertData, setAlertData] = useState(null);
  const [email, setEmail] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginPath, setLoginPath] = useState('/login');
  const isPackageWithLandingPage = MEMBERSHIPS_WITH_LANDING_PAGE.includes(packageShortName);

  useEffect(() => {
    if (window.location.search) {
      if (window.location.search.includes('return_to')) {
        setLoginPath(`/login${window.location.search}`);
      } else {
        setLoginPath(`/login?return_to=${window.location.href}`);
      }
    }
  }, []);

  useEffect(() => {
    if (isSignedIn) {
      navigate(`${REGISTER_PATH}/${packageShortName}/get-started${window.location.search}`);
    } else {
      setIsCheckingInitialRedirection(false);
    }
  }, [isSignedIn]);

  /**
   * When the form is submitted, it does a request to verify if user is valid or not:
   * - If not valid (email already exists) it displays an error message.
   * - If valid, it does a register user request and it sets setEmailRecipient
   *   in order to display the correct message to the user.
   *
   * @param {Event} event - Event triggered by browser
   * @returns {undefined}
   */
  const requestEmailVerification = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      const checkUserResponse = await checkUserEmail(email);
      if (!checkUserResponse.valid) {
        setEmailExists(true);
        setIsLoading(false);
        return;
      }

      await registerUserRequest({ email, packageShortName, paymentPage, returnTo });
      setIsLoading(false);
      setEmailRecipient(email);
      navigate(`${REGISTER_PATH}/${packageShortName}/verification-sent`);
    } catch {
      setIsLoading(false);
      setAlertData({ type: 'error', message: 'Error on registration, please try again.' });
    }
  };

  const renderText1 = Boolean(text1) && (
    <h1
      data-testid='text-1'
      className={classNames('text-1', {
        'fs-3 mb-0': isPackageWithLandingPage,
        'mb-3 text-center fs-2 fw-bold text-center': !isPackageWithLandingPage,
      })}
    >
      {text1}
      {isPackageWithLandingPage && <span className='text-orange-devex'>.</span>}
    </h1>
  );

  const renderText2 = Boolean(text2) && (
    <div data-testid='text-2' className='text-2'>
      {text2}
      {isPackageWithLandingPage && <span className='text-orange-devex'>.</span>}
    </div>
  );

  const renderAccountBenefits = (
    <div
      className={`text-gray-li small-text ${
        isPackageWithLandingPage ? 'mt-3' : 'mb-45 text-center'
      }`}
    >
      Register here to get access to the community, news and information, and updates about benefits
      and other offers.
    </div>
  );

  const socialReturnTo = `${REGISTER_PATH}/${packageShortName}/your-details${createQueryString({
    package_short_name: packageShortName,
    payment_page_permalink: paymentPage,
    return_to: returnTo,
  })}`;

  const renderSocialLogins = (
    <div data-testid='social-logins' className='mt-3 mb-3'>
      <SocialLogin
        provider={'google_oauth2'}
        authToken={authToken}
        buttonText='Sign up with Google'
        iconClass='icon-google'
        redirectTo={socialReturnTo}
      />
      <SocialLogin
        provider={'microsoft_graph'}
        authToken={authToken}
        buttonText='Sign up with Microsoft'
        iconClass='icon-microsoft'
        redirectTo={socialReturnTo}
      />
      <SocialLogin
        provider={'linkedin'}
        authToken={authToken}
        buttonText='Sign up with LinkedIn'
        iconClass='icon-linkedin'
        redirectTo={socialReturnTo}
      />
      <SocialLogin
        provider={'facebook'}
        authToken={authToken}
        buttonText='Sign up with Facebook'
        iconClass='icon-facebook-square'
        redirectTo={socialReturnTo}
      />
    </div>
  );

  const renderSeparator = (
    <section className='separator mt-3 mb-3'>
      <span className='separator-title'>or use your email</span>
    </section>
  );

  const renderUserAgreement = (
    <div className={`mt-2 ${!isPackageWithLandingPage && 'mb-45'}`}>
      <TermsAndConditions />
    </div>
  );

  const renderExistingEmailError = (
    <div data-testid='existing-email-error' className='existing-email-error mt-2'>
      This email is already associated with an existing Devex account.
      <a className='btn-link ms-1' href={loginPath}>
        Sign in
      </a>
      .
    </div>
  );

  const renderSignInLink = (
    <a className='btn-link link-blue-orange text-center d-block' href={loginPath}>
      Sign in to your account
    </a>
  );

  const renderEmailForm = (
    <form role='form' onSubmit={requestEmailVerification}>
      <div className='form-group mb-3'>
        <label className='form-label d-none d-sm-none d-md-block' htmlFor='register-email'>
          Email
        </label>
        <input
          id='register-email'
          data-testid='register-email'
          type='email'
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          className='form-control'
        ></input>
        {emailExists && renderExistingEmailError}
      </div>
      {renderUserAgreement}
      <button data-testid='register-button' type='submit' className='btn btn-dark w-100 mt-3 mb-3'>
        Get started
      </button>
      {renderSignInLink}
      <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
    </form>
  );

  if (isCheckingInitialRedirection) {
    return null;
  }

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div data-testid='default-register-form' className='default-register-form'>
        {renderText1}
        {renderText2}
        {renderAccountBenefits}
        {renderSocialLogins}
        {renderSeparator}
        {renderEmailForm}
      </div>
    </>
  );
};

DefaultRegisterForm.propTypes = {
  setEmailRecipient: PropTypes.func.isRequired,
};

export default DefaultRegisterForm;
